import { createSlice } from '@reduxjs/toolkit';

const VALID_ROLE_PREFIXES = ['PLATFORM_', 'CLIENT_', 'VENDOR_'];

function loadStoredAuth() {
  try {
    const token = localStorage.getItem('accessToken');
    const user  = JSON.parse(localStorage.getItem('user') || 'null');
    // Evict stale state if stored role is from old schema (e.g. "SUPER_ADMIN", "CLIENT", "VENDOR")
    const roleOk = user?.role && VALID_ROLE_PREFIXES.some(p => user.role.startsWith(p));
    if (token && !roleOk) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      return { user: null, accessToken: null, isAuthenticated: false };
    }
    return { user, accessToken: token, isAuthenticated: !!token && !!user };
  } catch {
    return { user: null, accessToken: null, isAuthenticated: false };
  }
}

const initialState = loadStoredAuth();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(state, action) {
      const { accessToken, refreshToken, ...user } = action.payload;
      state.user        = user;
      state.accessToken = accessToken;
      state.isAuthenticated = true;
      localStorage.setItem('accessToken',  accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    },
    logout(state) {
      state.user        = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
  },
});

export const { setCredentials, logout } = authSlice.actions;
export default authSlice.reducer;

// ── Base selectors ────────────────────────────────────────────────────────────
export const selectCurrentUser  = (state) => state.auth.user;
export const selectIsAuth       = (state) => state.auth.isAuthenticated;
export const selectUserRole     = (state) => state.auth.user?.role;
export const selectCompanyId    = (state) => state.auth.user?.companyId;
export const selectCompanyName  = (state) => state.auth.user?.companyName;

// ── Role-group helpers ────────────────────────────────────────────────────────
// Platform roles  : PLATFORM_SUPER_ADMIN | PLATFORM_ADMIN | PLATFORM_USER | PLATFORM_VIEWER
// Client roles    : CLIENT_ADMIN | CLIENT_USER | CLIENT_VIEWER
// Vendor roles    : VENDOR_ADMIN | VENDOR_USER | VENDOR_VIEWER
// Admin roles     : *_ADMIN  (includes PLATFORM_SUPER_ADMIN & PLATFORM_ADMIN)
// Viewer roles    : *_VIEWER (read-only — no create / edit / delete)

export const selectIsPlatformUser  = (state) => state.auth.user?.role?.startsWith('PLATFORM_');
export const selectIsClientUser    = (state) => state.auth.user?.role?.startsWith('CLIENT_');
export const selectIsVendorUser    = (state) => state.auth.user?.role?.startsWith('VENDOR_');

export const selectIsViewer = (state) => state.auth.user?.role?.endsWith('_VIEWER');
export const selectIsAdmin  = (state) => {
  const role = state.auth.user?.role;
  return role === 'PLATFORM_SUPER_ADMIN' || role === 'PLATFORM_ADMIN'
      || role === 'CLIENT_ADMIN' || role === 'VENDOR_ADMIN'
      || role === 'CLIENT_VENDOR_ADMIN';
};
export const selectIsSuperAdmin = (state) => state.auth.user?.role === 'PLATFORM_SUPER_ADMIN';
export const selectHasSuperAdminAccess = (state) => {
  const role = state.auth.user?.role;
  return role === 'PLATFORM_SUPER_ADMIN' || role === 'CLIENT_VENDOR_ADMIN';
};

// Convenience: returns the portal base path for the current role.
// Returns null for unknown/stale roles — callers must handle null.
export const selectPortalBase = (state) => {
  const role = state.auth.user?.role;
  if (!role) return null;
  if (role === 'CLIENT_VENDOR_ADMIN')  return '/platform'; // uses platform portal
  if (role.startsWith('PLATFORM_')) return '/platform';
  if (role.startsWith('CLIENT_'))   return '/client';
  if (role.startsWith('VENDOR_'))   return '/vendor';
  return null; // stale / unknown role — force re-login
};
